<script setup lang="ts">
/**
 * Capsule card redesign — RedisignHomePage `15:9216`
 * Look canvas + intra-capsule look carousel · Default/Hover · Default/Mobile size
 */
import { computed, ref, watch } from 'vue'
import type { RouteLocationRaw } from 'vue-router'
import { RouterLink } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { FairIconButton } from '@/ui/primitives/FairIconButton'
import {
  fairCapsuleCardV2NavButtonSize,
  fairCapsuleCardV2ShowsChrome,
  normalizeFairCapsuleCardV2LookIndex,
  type FairCapsuleCardV2Item,
  type FairCapsuleCardV2Look,
  type FairCapsuleCardV2PreviewState,
  type FairCapsuleCardV2Size,
} from '@/ui/tokens/capsule-card-v2.data'

interface FairCapsuleCardV2Props {
  title?: string
  description?: string
  itemCount?: number
  looks?: FairCapsuleCardV2Look[]
  lookIndex?: number
  size?: FairCapsuleCardV2Size
  interactive?: boolean
  previewState?: FairCapsuleCardV2PreviewState
  ariaLabel?: string
  to?: RouteLocationRaw
}

const props = withDefaults(defineProps<FairCapsuleCardV2Props>(), {
  title: 'Fall in the city',
  description: 'Relaxed essentials for everyday wear, blending style and ease.',
  itemCount: 12,
  looks: () => [],
  lookIndex: 0,
  size: 'default',
  interactive: true,
  previewState: undefined,
  ariaLabel: undefined,
  to: undefined,
})

const emit = defineEmits<{
  click: [event: MouseEvent]
  'update:lookIndex': [index: number]
  'prev-look': [event: MouseEvent]
  'next-look': [event: MouseEvent]
}>()

const { t } = useI18n()

const isHovered = ref(false)

const totalLooks = computed(() => Math.max(1, props.looks.length))

const currentLookIndex = computed(() =>
  normalizeFairCapsuleCardV2LookIndex(props.lookIndex, totalLooks.value),
)

/** Slide direction for the look transition: next → content enters from the right. */
const lookDirection = ref<'next' | 'prev'>('next')

watch(currentLookIndex, (next, prev) => {
  const total = totalLooks.value
  if (total <= 1 || next === prev) return
  const forward = (prev + 1) % total === next
  const backward = (next + 1) % total === prev
  lookDirection.value = backward && !forward ? 'prev' : 'next'
})

const lookTransitionName = computed(
  () => `fair-capsule-card-v2-look-${lookDirection.value}`,
)

const currentLook = computed(() => {
  if (props.looks.length === 0) return null
  return props.looks[currentLookIndex.value] ?? props.looks[0]!
})

const currentItems = computed((): FairCapsuleCardV2Item[] => {
  return currentLook.value?.items ?? []
})

const resolvedState = computed(() => {
  if (props.previewState) return props.previewState
  return isHovered.value ? 'hovered' : 'default'
})

const showsChrome = computed(() =>
  fairCapsuleCardV2ShowsChrome(
    props.size,
    resolvedState.value,
    totalLooks.value,
  ),
)

const navButtonSize = computed(() =>
  fairCapsuleCardV2NavButtonSize(props.size),
)

const counterLabel = computed(
  () => `${currentLookIndex.value + 1}/${totalLooks.value}`,
)

const itemsCountLabel = computed(() =>
  t('fair_home_v2.capsule.items_count', { count: props.itemCount }),
)

const rootClass = computed(() => ({
  'fair-capsule-card-v2': true,
  [`fair-capsule-card-v2--${props.size}`]: true,
  [`fair-capsule-card-v2--${resolvedState.value}`]: true,
  'fair-capsule-card-v2--non-interactive': !props.interactive,
}))

function itemClass(item: FairCapsuleCardV2Item): Record<string, boolean> {
  return {
    'fair-capsule-card-v2__item': true,
    [`fair-capsule-card-v2__item--${item.slot}`]: true,
  }
}

function itemImgClass(item: FairCapsuleCardV2Item): Record<string, boolean> {
  return {
    'fair-capsule-card-v2__item-img': true,
    'fair-capsule-card-v2__item-img--cover': item.fit === 'cover',
  }
}

function setLookIndex(index: number) {
  const next = normalizeFairCapsuleCardV2LookIndex(index, totalLooks.value)
  emit('update:lookIndex', next)
}

function handlePrev(event: MouseEvent) {
  if (!props.interactive) return
  event.stopPropagation()
  event.preventDefault()
  setLookIndex(currentLookIndex.value - 1)
  emit('prev-look', event)
}

function handleNext(event: MouseEvent) {
  if (!props.interactive) return
  event.stopPropagation()
  event.preventDefault()
  setLookIndex(currentLookIndex.value + 1)
  emit('next-look', event)
}

function handleRootClick(event: MouseEvent) {
  if (!props.interactive) return
  emit('click', event)
}

function onPointerEnter() {
  if (!props.interactive || props.previewState) return
  isHovered.value = true
}

function onPointerLeave() {
  if (!props.interactive || props.previewState) return
  isHovered.value = false
}
</script>

<template>
  <component
    :is="to ? RouterLink : 'article'"
    :class="rootClass"
    :to="to"
    :aria-label="ariaLabel ?? title"
    @click="handleRootClick"
    @pointerenter="onPointerEnter"
    @pointerleave="onPointerLeave"
  >
    <div class="fair-capsule-card-v2__look-shell">
      <Transition :name="lookTransitionName">
        <div
          :key="currentLookIndex"
          class="fair-capsule-card-v2__look"
          aria-hidden="true"
        >
          <div
            v-for="item in currentItems"
            :key="item.id"
            :class="itemClass(item)"
          >
            <img
              :class="itemImgClass(item)"
              :src="item.imageUrl"
              :alt="item.alt ?? ''"
              loading="lazy"
              draggable="false"
            >
          </div>
        </div>
      </Transition>

      <p
        v-if="totalLooks > 1"
        class="fair-capsule-card-v2__counter"
        :aria-hidden="!showsChrome"
      >
        {{ counterLabel }}
      </p>

      <FairIconButton
        v-if="totalLooks > 1"
        class="fair-capsule-card-v2__nav fair-capsule-card-v2__nav--prev"
        icon="arrow-back"
        :size="navButtonSize"
        :interactive="interactive && showsChrome"
        :aria-hidden="!showsChrome"
        :tabindex="showsChrome ? 0 : -1"
        :aria-label="t('fair_home_v2.capsule.prev_look')"
        @click="handlePrev"
      />

      <FairIconButton
        v-if="totalLooks > 1"
        class="fair-capsule-card-v2__nav fair-capsule-card-v2__nav--next"
        icon="arrow-forward"
        :size="navButtonSize"
        :interactive="interactive && showsChrome"
        :aria-hidden="!showsChrome"
        :tabindex="showsChrome ? 0 : -1"
        :aria-label="t('fair_home_v2.capsule.next_look')"
        @click="handleNext"
      />
    </div>

    <div class="fair-capsule-card-v2__content">
      <div class="fair-capsule-card-v2__text">
        <p class="fair-capsule-card-v2__title">
          {{ title }}
        </p>
        <p class="fair-capsule-card-v2__description">
          {{ description }}
        </p>
      </div>
      <p class="fair-capsule-card-v2__count">
        {{ itemsCountLabel }}
      </p>
    </div>
  </component>
</template>
